/**
 * One hour of the Score lane: the bar (taller = better; purple inside the
 * container's hours, grey outside — never used), gold 1-2-3 on the day's
 * best hours wherever they are, the Flow Optimizer's red excluded mark on an
 * hour a rule excluded, hover to read, click to pin.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import { cn } from '@/lib/utils';

import { hourRange, shadeFor, type HourView } from '@/features/drop-generator/components/editor/general/hour/hour-chart-math';
import { excludeHit } from '@/features/drop-generator/components/editor/rules/rule-model';
import { ExcludedMark } from '@/features/drop-generator/components/editor/rules/RuleHits';

interface Props {
  row: HourView;
  rankedHours: number;
  active: boolean;
  pinned: boolean;
  onHover: (hour: number | null) => void;
  onPin: (hour: number) => void;
}

export const ScoreColumn: FC<Props> = ({ row, rankedHours, active, pinned, onHover, onPin }) => (
  <button
    type="button"
    aria-label={`${hourRange(row.hour)} — ${row.unranked ? 'excluded — not judged yet' : `rank ${row.rank} of ${rankedHours}`}${row.outsideLabel ? `, ${row.outsideLabel}` : ''}`}
    aria-pressed={pinned}
    onMouseEnter={() => onHover(row.hour)}
    onFocus={() => onHover(row.hour)}
    onClick={() => onPin(row.hour)}
    className={cn(
      'relative flex h-full flex-col justify-end rounded-sm px-px outline-none transition-colors duration-200 focus-visible:ring-1 focus-visible:ring-primary',
      active && 'bg-primary/10',
      pinned && 'ring-1 ring-inset ring-primary',
    )}
  >
    {row.rank !== null && row.rank <= 3 ? (
      <span
        title={`#${row.rank} hour of the day`}
        className={cn('mx-auto mb-0.5 hidden h-4 w-4 items-center justify-center rounded-full bg-warning font-mono text-[10px] font-bold text-warning-foreground shadow-sm animate-dg-hour-pop motion-reduce:animate-none sm:flex', row.rank === 1 && 'animate-dg-tut-breathe')}
        style={{ animationDelay: `${600 + row.rank * 120}ms` }}
      >
        {row.rank}
      </span>
    ) : null}
    {row.unranked && excludeHit(row.ruleHits) ? <ExcludedMark className="mx-auto mb-0.5 animate-in fade-in-0 zoom-in-75" /> : null}
    <span
      className="mx-auto w-[64%] origin-bottom rounded-t-sm transition-[height,background-color] duration-500 ease-out animate-dg-hour-grow motion-reduce:animate-none"
      style={{
        height: `${Math.max(row.unranked ? 0 : 3, row.scorePct)}%`,
        background: row.lit ? `hsl(var(--primary) / ${shadeFor(row.rank, rankedHours)})` : 'hsl(var(--muted-foreground) / 0.2)',
        animationDelay: `${row.hour * 25}ms`,
      }}
    />
  </button>
);
